import { useEffect, useState } from "react";

import * as xlsx from "xlsx";
import { type LocalFile, type ParseResult, parse } from "papaparse";
import { useResetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";

import { ParticipantTypes, ResaleInput, ResaleStatuses } from "@api/graphql";
import { mapArtistDetails, mapArtworkDetails, mapResaleDetails } from "@common/csvUtils/Mapping";
import { type ImportedResaleInput, type ValidationItems, validateImport } from "@common/csvUtils/ResaleSchema";
import { validateResaleStatus } from "@common/operations/validateResale";
import type { CsvInput } from "@components/resale_submission/csv/UploadCsv";
import { resaleFormData } from "@states/resale_submission";
import { isCMO } from "@util/index";

export interface ValidatedResaleInput extends ResaleInput {
  validation?: ValidationItems;
  resaleStatus?: ResaleStatuses;
}

export const parseCSV = (
  file: CsvInput | undefined,
  participant_id: string,
  isParticipantCMO: boolean,
): Promise<ValidatedResaleInput[] | undefined> => {
  return new Promise((resolve, reject) => {
    if (file?.file !== undefined) {
      console.log(file.file.name);
      const parseFile = file.file as LocalFile;
      const fileType = file?.file.name;

      if (fileType.endsWith(".csv")) {
        parse(parseFile, {
          header: true,
          transformHeader: (header: string) => {
            //need this due to csv file containing human readable formatting and special characters (ie spaces, Capitalisation, and colons), and instructions in parenthesis.
            //trims the string to remove trailing or leading whitespace, and uses regex to find internal whitespaces, and replaces with underscores, removes colons, and removes text inside parenthesis.
            return header
              .toLowerCase()
              .replace(/ *\([^)]*\) */g, "")
              .trim() // this needs to come after removing parenthesis text to remove any remaining whitespaces
              .replace(/\s/g, "_")
              .replace(/:/g, "");
          },
          delimiter: ",",
          download: false,
          skipEmptyLines: true,
          transform: (value) => {
            return value === "" ? undefined : value; // this is because the 'dynamic typing' returns an empty string as null, and the models don't accept null, only undefined.
          },
          dynamicTyping: true,
          complete: (results: ParseResult<ImportedResaleInput>) => {
            try {
              if (!results?.data) {
                reject(new Error("No data found in CSV file"));
                return;
              }
              const validatedData = validateAndmapDataToResaleInput(results.data, participant_id, isParticipantCMO);
              resolve(validatedData);
            } catch (error) {
              reject(error);
            }
          },
        });
      }

      if (fileType.endsWith(".xlsx") || fileType.endsWith(".xls")) {
        console.log("Excel file identified");
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = xlsx.read(data, { type: "array" });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const jsonData = xlsx.utils.sheet_to_json<string[]>(worksheet, {
              header: 1,
              defval: undefined,
            });
            const [rawHeaders, ...rows] = jsonData;
            console.log(rawHeaders);
            const headers: string[] = [];
            rawHeaders.forEach((xlsheader: string) => {
              const normalizedHeader = xlsheader
                .toLowerCase()
                .replace(/ *\([^)]*\) */g, "")
                .trim()
                .replace(/\s/g, "_")
                .replace(/:/g, "");
              headers.push(normalizedHeader);
            });
            const dataObjects: ImportedResaleInput[] = rows.map((row) => {
              const obj: Record<string, unknown> = {};
              headers.forEach((key, idx) => {
                obj[key] = row[idx];
                if (key === "date_sold" && typeof row[idx] === "number") {
                  obj["date_sold"] = excelDateToDDMMYYYY(row[idx]);
                } else if (key === "sale_price") {
                  obj["sale_price"] = String(row[idx]).replace(/[$,]/g, "").trim();
                } else if (key === "royalty_amount") {
                  obj["royalty_amount"] = String(row[idx]).replace(/[$,]/g, "").trim();
                }
              });
              return obj as ImportedResaleInput;
            });
            console.log(dataObjects);
            const validatedData = validateAndmapDataToResaleInput(dataObjects, participant_id, isParticipantCMO);
            resolve(validatedData);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error("Failed to read Excel file"));
        reader.readAsArrayBuffer(file.file);
      }
    } else {
      resolve(undefined);
    }
  });
};

function excelDateToJSDate(serial: number): Date {
  const excelEpoch = new Date(Date.UTC(1899, 11, 30)); // 1900-01-00
  return new Date(excelEpoch.getTime() + Math.floor(serial) * 86400 * 1000);
}

function formatDateToDDMMYYYY(date: Date): string {
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

//Convert Excel serial date to JS Date
function excelDateToDDMMYYYY(serial: number): string {
  return formatDateToDDMMYYYY(excelDateToJSDate(serial));
}

const validateAndmapDataToResaleInput = (
  dataToMap: ImportedResaleInput[],
  participant_id: string,
  isParticipantCMO: boolean,
): ValidatedResaleInput[] | undefined => {
  if (dataToMap !== undefined) {
    return dataToMap.map((importedResale) => {
      const validationSummary = validateImport(importedResale, isParticipantCMO);
      const artist = mapArtistDetails(validationSummary.result);
      const artwork = mapArtworkDetails(validationSummary.result);
      const resale = mapResaleDetails(validationSummary.result);

      function isCMOValidationResult(
        result: typeof validationSummary.result,
      ): result is typeof validationSummary.result & { royalty_amount: number; artist_ipi: string } {
        return "royalty_amount" in result && "artist_ipi" in result;
      }

      const resaleStatus = isCMOValidationResult(validationSummary.result)
        ? !Number.isNaN(Number(validationSummary.result.royalty_amount)) // valibot should have covered royalty_amount as a mandatory field
          ? ResaleStatuses.PROVISIONALLY_ELIGIBLE
          : undefined
        : validateResaleStatus(
            new Date(validationSummary.result.date_sold),
            validationSummary.result.sale_price || 0,
            validationSummary.result.seller_acquired_work_post_8th_june_2010,
            artist,
          );

      const additionalFields = isCMOValidationResult(validationSummary.result)
        ? {
            royalty_payable: validationSummary.result.royalty_amount,
            ipi_basenumber: validationSummary.result.artist_ipi,
          }
        : {};

      return {
        artist: artist,
        artwork: artwork,
        resale: resale,
        validation: validationSummary.validation,
        resaleStatus: resaleStatus,
        batch_discriminator: uuidv4(),
        vendor_id: Number.parseInt(participant_id),
        ...additionalFields,
      } as ValidatedResaleInput;
    });
  }
};

export const useParseAndValidateCSV = (
  file: CsvInput | undefined,
  participant_id: string,
  participant_types?: ParticipantTypes[],
) => {
  const resetList = useResetRecoilState(resaleFormData);

  const [resaleInput, setResaleInput] = useState<ValidatedResaleInput[]>();
  const [success, setSuccess] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const isParticipantCMO = isCMO(participant_types);

  useEffect(() => {
    resetList(); // reset cart if new file uploaded
    let isMounted = true; // for cleanup/prevent memory leaks

    const parse = async () => {
      try {
        setLoading(true);
        setError(null);

        const parseResult = await parseCSV(file, participant_id, isParticipantCMO);

        if (!isMounted) {
          return;
        } // prevent setting state if component unmounted

        if (parseResult) {
          setResaleInput(parseResult);
          setSuccess(true);
        } else {
          setSuccess(false);
          setResaleInput(undefined);
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setError(err instanceof Error ? err : new Error("Failed to parse CSV"));
        setSuccess(false);
        setResaleInput(undefined);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    parse();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [file, isParticipantCMO, participant_id, resetList]);

  return {
    resaleInput,
    success,
    loading,
    error,
    isParticipantCMO,
  };
};
